import React, { createContext, useContext } from 'react';

/**
 * OverarchingEditable context
 *
 * Broadcasts one boolean, `canEdit`, down into every section of the Company
 * Settings page. When `canEdit` is false (e.g. Employee role — has
 * `roros.overarching.view` but not `roros.overarching.manage`), sections
 * should hide every edit affordance: Save buttons, Add / Edit / Delete
 * icons, reorder arrows, Research-with-AI button, etc. Inputs are shown
 * read-only so the data is still visible.
 *
 * The backend enforces the same gate — this context only exists to keep the
 * UI honest and to stop users from clicking buttons that would 403.
 */
interface OverarchingEditableValue {
  canEdit: boolean;
}

const OverarchingEditableContext = createContext<OverarchingEditableValue>({ canEdit: true });

export function OverarchingEditableProvider({
  canEdit,
  children,
}: {
  canEdit: boolean;
  children: React.ReactNode;
}) {
  return (
    <OverarchingEditableContext.Provider value={{ canEdit }}>
      {children}
    </OverarchingEditableContext.Provider>
  );
}

/**
 * Returns `{ canEdit }` from the nearest provider. Defaults to `canEdit: true`
 * so components used outside the Company Settings tree (e.g. in tests or other
 * pages) keep their normal editing behaviour.
 */
export function useOverarchingEditable(): OverarchingEditableValue {
  return useContext(OverarchingEditableContext);
}
